import React, { MutableRefObject, useEffect, useRef } from 'react';
import { CANVAS_STYLE } from '../utils/ContactListeningConstants';
import { MERIDIAN_COLORS } from '../../../Util';
import {
  CanvasState,
  SampledAudio,
  CANVAS_BAR_WIDTH,
} from '../utils/ContactListeningHelpers';

export interface WaveCanvasProps {
  id?: string;
  sampledAudio?: any;
  currentTime: number;
  setRatio: (number: number) => void;
  canvasState: CanvasState;
  setCanvasDrawn: any;
}

const WaveCanvas: React.FC<WaveCanvasProps> = ({
  id,
  sampledAudio,
  currentTime,
  setRatio,
  canvasState,
  setCanvasDrawn,
}: WaveCanvasProps): React.ReactElement => {
  const canvasRef = useRef() as MutableRefObject<HTMLCanvasElement>;

  useEffect(() => {
    const drawSingleDataPoint = (
      canvas: HTMLCanvasElement,
      context: CanvasRenderingContext2D,
      sampledAudio: SampledAudio,
      px: number
    ) => {
      if (sampledAudio) {
        const samples = sampledAudio.leftSamples.length;
        const width = canvas.width;

        const firstSample = Math.floor((px * samples) / width);
        let endSample = Math.ceil((px * samples) / width);
        if (endSample === firstSample) {
          endSample = endSample + 1;
        }

        // draw rectangle bars for each sample
        const left: number = Math.max(
          ...sampledAudio.leftSamples.slice(firstSample, endSample)
        );
        const right: number = Math.max(
          ...sampledAudio.rightSamples.slice(firstSample, endSample)
        );

        const leftHeight = (80 * left) / sampledAudio.leftMax;
        const rightHeight = (80 * right) / sampledAudio.rightMax;
        context.fillStyle = MERIDIAN_COLORS.MERIDIAN_COLOR_BLUE_DARK;
        context.fillRect(
          px,
          canvas.height / 2 - leftHeight,
          CANVAS_BAR_WIDTH,
          leftHeight
        );
        context.fillRect(px, canvas.height / 2, CANVAS_BAR_WIDTH, rightHeight);

        // underline silence gaps
        if (
          sampledAudio.gapsArray[firstSample] &&
          sampledAudio.gapsArray[firstSample] !== -1
        ) {
          context.fillStyle = MERIDIAN_COLORS.MERIDIAN_COLOR_BLUE_LIGHT;
          context.fillRect(
            px,
            canvas.height - CANVAS_BAR_WIDTH,
            CANVAS_BAR_WIDTH,
            CANVAS_BAR_WIDTH
          );
          context.fillStyle = MERIDIAN_COLORS.MERIDIAN_COLOR_DEFAULT;
        }
      }
    };

    const canvas = canvasRef.current;
    if (canvas) {
      if (canvas.width !== canvas.getBoundingClientRect().width) {
        // make canvas internal dimensions match external dimensions
        canvas.width = canvas.getBoundingClientRect().width;
        canvas.height = canvas.getBoundingClientRect().height;
      }
      const context = canvasRef.current.getContext('2d');
      if (canvasState === CanvasState.READY) {
        if (context) {
          // draw a red line to indicate current playing time
          context.clearRect(0, 0, canvas.width, canvas.height);
          context.strokeRect(0, 0, canvas.width, canvas.height);
          if (sampledAudio) {
            for (let i = 0; i < canvas.width; i += CANVAS_BAR_WIDTH) {
              drawSingleDataPoint(canvas, context, sampledAudio, i);
            }

            const ratio = currentTime / sampledAudio.duration;
            const currentX = ratio * canvas.width;

            context.fillStyle = MERIDIAN_COLORS.MERIDIAN_COLOR_RED;
            context.fillRect(currentX, 0, 1, canvas.height);
            context.fillStyle = MERIDIAN_COLORS.MERIDIAN_COLOR_DEFAULT;
          }
          setCanvasDrawn(true);
        }
      } else {
        if (context) {
          context.clearRect(0, 0, canvas.width, canvas.height);
          setCanvasDrawn(false);
        }
      }
    }
  }, [canvasRef, currentTime, sampledAudio, canvasState, id]);

  interface Point {
    x: number;
    y: number;
  }

  const getMousePos = (canvas: any, event: any): Point => {
    const rect = canvas.getBoundingClientRect();
    return {
      x: event.clientX - rect.left,
      y: event.clientY - rect.top,
    };
  };

  const onClickHandler = (event: any) => {
    const canvas = canvasRef.current;
    const pos = getMousePos(canvas, event);
    const ratio = pos.x / canvas.getBoundingClientRect().width;
    setRatio(ratio);
  };

  return (
    <canvas
      style={CANVAS_STYLE}
      ref={canvasRef}
      onClick={(event): void => onClickHandler(event)}
    />
  );
};
export default WaveCanvas;
